import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SEO = ({ 
  title = 'GENMOZPRO - Professional Payment Testing Tools',
  description = 'Professional suite for developers: BIN Generator, Check Live and Check Stripe. Test payment systems with precision using Luhn algorithm.',
  keywords = 'bin generator, card checker, stripe connector, payment testing, luhn algorithm, credit card validator, developer tools, payment integration testing',
  canonical = 'https://genmozpro.com',
  image = 'https://genmozpro.com/og-image.png'
}) => {
  const location = useLocation();
  const fullUrl = `https://genmozpro.com${location.pathname}`;

  useEffect(() => {
    // Google Analytics - Track pageview
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('config', 'G-517898989', {
        page_path: location.pathname,
        page_title: title,
        page_location: fullUrl
      });
      
      window.gtag('event', 'page_view', {
        page_title: title,
        page_location: fullUrl,
        page_path: location.pathname,
      });
      
      console.log('📊 GA4 Pageview tracked:', location.pathname);
    } else {
      console.warn('⚠️ Google Analytics not loaded yet');
    }

    // Google AdSense - signal page change
    if (typeof window !== 'undefined' && window.adsbygoogle) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        console.log('📢 AdSense page change signaled');
      } catch (e) {
        console.error('❌ AdSense error:', e);
      }
    }
  }, [location.pathname, title, fullUrl]);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonical || fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="GENMOZPRO" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="GENMOZPRO" />
      <meta name="theme-color" content="#EAB308" />
      <meta name="msapplication-TileColor" content="#000000" />
      
      {/* Geo Tags */}
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="United States" />
      
      {/* Google Site Verification */}
      <meta name="google-site-verification" content="SfvOFZKHkmp5Cw4JCCnzzT2INvVASUDHcDQhzSmri3U" />
      
      {/* Google AdSense Account Verification */}
      <meta name="google-adsense-account" content="ca-pub-7427348839882670" />
      
      {/* Structured Data for Google */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "GENMOZPRO",
          "description": description,
          "url": fullUrl,
          "applicationCategory": "DeveloperApplication",
          "operatingSystem": "Any",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
          }
        })}
      </script>
      
      {/* Google Analytics & AdSense */}
      <script
        async
        src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7427348839882670"
        crossOrigin="anonymous"
      />
      
      {/* Preconnect to improve performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      <link rel="dns-prefetch" href="https://www.google-analytics.com" />
      <link rel="dns-prefetch" href="https://pagead2.googlesyndication.com" />
    </Helmet>
  );
};

export default SEO;